import * as React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import AddNewVersionModal from 'src/features/reservations/components/modals/AddNewVersionModal';
import CopyVersionModal from 'src/features/reservations/components/modals/CopyVersionModal';
import DeleteVersion from 'src/features/reservations/components/modals/DeleteVersion';
import EditVersionModal from 'src/features/reservations/components/modals/EditVersionModal';
import { ReservationVersionTableColumns } from 'src/features/reservations/components/tables/utils/reservationVersionsTableColumns';
import { useDeleteReservationType } from 'src/features/reservations/hooks/useDeleteReservationType';
import { useReservationTypeList } from 'src/features/reservations/hooks/useReservationTypeList';
import { ReservationVersionsProvider } from 'src/features/reservations/providers/ReservationVersionsProvider';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { generalErrorMessages } from 'src/utils/messages';

import type { ReservationType } from 'src/features/reservations/types/reservationType';

const ReservationVersions = () => {
    const { formatMessage } = useIntl();
    const { reservationType } = useParams();
    const { parkingId } = useParams();

    const [isDeleteVersionModalOpen, setIsDeleteVersionModalOpen] = React.useState(false);
    const [isEditVersionModalOpen, setIsEditVersionModalOpen] = React.useState(false);
    const [isCopyVersionModalOpen, setIsCopyVersionModalOpen] = React.useState(false);

    const [selectedVersion, setSelectedVersion] = React.useState<ReservationType | undefined>(undefined);
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: true }],
    });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const permissionReservationsSettings = useHasPermissions([PERMISSION.RESERVATIONS_SETTINGS]);

    const {
        data: reservationData,
        refetch,
        isLoading,
        isError,
    } = useReservationTypeList({
        parkingId: parkingId || '',
        reservationType,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId },
    });

    const { mutate: deleteVersion } = useDeleteReservationType({
        onSuccess: (_) => {
            void refetch();
            setSelectedVersion(undefined);
            toast({
                title: formatMessage(reservationMessages.deleteVersionSuccess),
                description: formatMessage(reservationMessages.deleteVersionSuccessDetails, {
                    versionName: selectedVersion?.name,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const handleVersionModal = (
        type: 'edit' | 'copy' | 'delete',
        version?: ReservationType,
        isOpen: boolean = false,
    ) => {
        setSelectedVersion(version);
        if (type === 'copy') {
            setIsCopyVersionModalOpen(isOpen);
        } else if (type === 'delete') {
            setIsDeleteVersionModalOpen(isOpen);
        } else {
            setIsEditVersionModalOpen(isOpen);
        }
    };

    const openDeleteVersionModal = (version: ReservationType) => handleVersionModal('delete', version, true);
    const closeDeleteVersionModal = () => handleVersionModal('delete', undefined);

    const openEditVersionModal = (version: ReservationType) => handleVersionModal('edit', version, true);
    const closeEditVersionModal = () => handleVersionModal('edit', undefined);

    const openCopyVersionModal = (version: ReservationType) => handleVersionModal('copy', version, true);
    const closeCopyVersionModal = () => handleVersionModal('copy', undefined);

    const columns = ReservationVersionTableColumns({
        openEditVersionModal,
        openDeleteVersionModal,
        openCopyVersionModal,
    });

    const newestValidFromDate = React.useMemo(() => {
        return reservationData?.results[0]?.valid_from || '';
    }, [reservationData]);

    return (
        <ReservationVersionsProvider>
            <DataTable
                columns={columns}
                data={reservationData}
                refetch={refetch}
                search={search}
                sorting={sorting}
                pagination={pagination}
                isError={isError}
                isLoading={isLoading}
                semantics={{
                    title: formatMessage(reservationMessages.reservationPlanVersions),
                    entryName: formatMessage(reservationMessages.version),
                    entryNames: formatMessage(reservationMessages.versions),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: permissionReservationsSettings ? AddNewVersionModal : undefined,
                }}
                enableLocalFilters={true}
            />
            {permissionReservationsSettings && selectedVersion && (
                <>
                    {isCopyVersionModalOpen && (
                        <CopyVersionModal
                            isCopyVersionModalOpen={isCopyVersionModalOpen}
                            closeCopyVersionModal={closeCopyVersionModal}
                            selectedVersion={{ ...selectedVersion, valid_from: newestValidFromDate }}
                            refetch={refetch}
                        />
                    )}
                    {isDeleteVersionModalOpen && (
                        <DeleteVersion
                            isDeleteVersionModalOpen={isDeleteVersionModalOpen}
                            closeDeleteVersionModal={closeDeleteVersionModal}
                            deleteVersion={deleteVersion}
                            selectedVersion={selectedVersion}
                        />
                    )}
                    {isEditVersionModalOpen && (
                        <EditVersionModal
                            isEditVersionModalOpen={isEditVersionModalOpen}
                            closeEditVersionModal={closeEditVersionModal}
                            selectedVersion={selectedVersion}
                            refetch={refetch}
                        />
                    )}
                </>
            )}
        </ReservationVersionsProvider>
    );
};

export default ReservationVersions;
